import { type FormEvent } from 'react';

import { CategoryDTO, CategoryDTOSchema, CategoryEntity, TagDTO, TagDTOSchema, TagEntity } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import QuestionIcon from '@/components/icons/question';
import { IssueSettingsSkeleton } from '@/components/settings/settings-form-skeleton';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/toaster';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePermissions } from '@/hooks/use-permissions';
import { getIssueCategoriesQueryOptions, getIssueTagsQueryOptions, issueCategoryKeys, issueTagKeys } from '@/queries/issues';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';

import IssueCategories from './issue-categories';
import IssueTags from './issue-tags';

const IssueSettingsFormSchema = z.object({
  categories: z.array(CategoryDTOSchema),
  tags: z.array(TagDTOSchema),
});

type IssueSettingsFormData = z.infer<typeof IssueSettingsFormSchema>;

const autoCloseFormSchema = z.object({
  autoCloseIssuesOnRepairSubmit: z.boolean(),
});

type AutoCloseFormData = z.infer<typeof autoCloseFormSchema>;

type IssueSettingsSaveResult = { settingName: string; succeeded: boolean };

function IssueSettingsForm({ defaultValues }: { defaultValues: IssueSettingsFormData }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { canEditWorkspaceSettings } = usePermissions();
  const { org, setOrg } = useUserStore();

  const {
    watch: watchCategoriesAndTags,
    setValue: setCategoriesAndTagsValue,
    handleSubmit: handleSubmitCategoriesAndTags,
    reset: resetCategoriesAndTags,
    formState: { isDirty: isCategoriesAndTagsDirty, isSubmitting: isCategoriesAndTagsSubmitting },
  } = useForm<IssueSettingsFormData>({
    resolver: zodResolver(IssueSettingsFormSchema),
    defaultValues,
  });

  const {
    control: autoCloseControl,
    handleSubmit: handleSubmitAutoClose,
    reset: resetAutoClose,
    formState: { isDirty: isAutoCloseDirty, isSubmitting: isAutoCloseSubmitting },
  } = useForm<AutoCloseFormData>({
    resolver: zodResolver(autoCloseFormSchema),
    defaultValues: { autoCloseIssuesOnRepairSubmit: org?.autoCloseIssuesOnRepairSubmit ?? true },
  });

  const categoriesAndTagsData = watchCategoriesAndTags();

  const { mutateAsync: updateIssueCategories, isPending: isCategoriesPending } = useMutation({
    mutationFn: async ({ categories }: { categories: CategoryDTO[] }) => await ApiService.UpdateCategories({ categories, entity: CategoryEntity.Issue }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: issueCategoryKeys.all,
      });
    },
  });

  const { mutateAsync: updateIssueTags, isPending: isTagsPending } = useMutation({
    mutationFn: async ({ tags }: { tags: TagDTO[] }) =>
      await ApiService.UpdateTags({
        tags,
        entity: TagEntity.Issue,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: issueTagKeys.all,
      });
    },
  });

  const { mutateAsync: updateAutoClose, isPending: isAutoClosePending } = useMutation({
    mutationFn: async (autoCloseIssuesOnRepairSubmit: boolean) => {
      if (!org) {
        throw new Error('Workspace is not loaded');
      }
      return (await (await ApiService.UpdateAutoCloseIssuesOnRepairSubmit({ workspaceId: org.id, autoCloseIssuesOnRepairSubmit })).json())
        .autoCloseIssuesOnRepairSubmit;
    },
  });

  function handleCategoriesChange(categories: CategoryDTO[]) {
    setCategoriesAndTagsValue('categories', categories, { shouldDirty: true });
  }

  function handleTagsChange(tags: TagDTO[]) {
    setCategoriesAndTagsValue('tags', tags, { shouldDirty: true });
  }

  const saveCategories = async (categories: CategoryDTO[]): Promise<IssueSettingsSaveResult> => {
    try {
      await updateIssueCategories({ categories: categories.map((category, index) => ({ ...category, order: index })) });
      return { settingName: 'Issue categories', succeeded: true };
    } catch {
      return { settingName: 'Issue categories', succeeded: false };
    }
  };

  const saveTags = async (tags: TagDTO[]): Promise<IssueSettingsSaveResult> => {
    try {
      await updateIssueTags({ tags: tags.map((tag, index) => ({ ...tag, order: index })) });
      return { settingName: 'Issue tags', succeeded: true };
    } catch {
      return { settingName: 'Issue tags', succeeded: false };
    }
  };

  const saveAutoClose = async (autoCloseIssuesOnRepairSubmit: boolean): Promise<IssueSettingsSaveResult> => {
    try {
      const result = await updateAutoClose(autoCloseIssuesOnRepairSubmit);
      if (org) {
        setOrg({ ...org, autoCloseIssuesOnRepairSubmit: result });
      }
      resetAutoClose({ autoCloseIssuesOnRepairSubmit: result });
      return { settingName: 'the auto-close setting', succeeded: true };
    } catch {
      return { settingName: 'the auto-close setting', succeeded: false };
    }
  };

  const isSavePending = isCategoriesPending || isTagsPending || isAutoClosePending;

  const isSaveDisabled = isCategoriesAndTagsSubmitting || isAutoCloseSubmitting || (!isCategoriesAndTagsDirty && !isAutoCloseDirty);

  const handleSave = async () => {
    let issueData: IssueSettingsFormData | undefined;
    let autoCloseData: AutoCloseFormData | undefined;

    // One Save, so one outcome: a half that fails validation abandons the whole save rather
    // than letting its sibling through and reporting success. Field errors say why.
    if (isCategoriesAndTagsDirty) {
      await handleSubmitCategoriesAndTags((validated) => {
        issueData = validated;
      })();
      if (!issueData) return;
    }

    if (isAutoCloseDirty) {
      await handleSubmitAutoClose((validated) => {
        autoCloseData = validated;
      })();
      if (!autoCloseData) return;
    }

    const issueSaves: Promise<IssueSettingsSaveResult>[] = [];
    const autoCloseSaves: Promise<IssueSettingsSaveResult>[] = [];

    if (issueData) {
      issueSaves.push(saveCategories(issueData.categories));
      issueSaves.push(saveTags(issueData.tags));
    }

    if (autoCloseData) {
      autoCloseSaves.push(saveAutoClose(autoCloseData.autoCloseIssuesOnRepairSubmit));
    }

    if (issueSaves.length === 0 && autoCloseSaves.length === 0) return;

    const [issueResults, autoCloseResults] = await Promise.all([Promise.all(issueSaves), Promise.all(autoCloseSaves)]);

    if (issueData && issueResults.every((result) => result.succeeded)) {
      resetCategoriesAndTags(issueData);
    }

    const failed = [...issueResults, ...autoCloseResults].filter((result) => !result.succeeded);

    if (failed.length === 0) {
      toast.success('Issue settings updated successfully');
      return;
    }

    toast.error(failed.length === 1 ? `Failed to update ${failed[0].settingName}` : 'Some changes could not be saved');
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleSave();
  };

  return (
    <form onSubmit={handleFormSubmit} className="flex flex-col gap-4">
      <IssueCategories onCategoriesChange={handleCategoriesChange} issueCategories={categoriesAndTagsData.categories} disabled={!canEditWorkspaceSettings} />
      <IssueTags onTagsChange={handleTagsChange} issueTags={categoriesAndTagsData.tags} disabled={!canEditWorkspaceSettings} />

      <div className="flex w-full items-center gap-4">
        <Label htmlFor="auto-close-issues" className="text-default-text w-[350px] shrink-0 text-sm font-normal">
          Auto-close Issue on record submission
        </Label>
        <div className="flex items-center gap-2">
          <Controller
            control={autoCloseControl}
            name="autoCloseIssuesOnRepairSubmit"
            render={({ field: { value, onChange } }) => (
              <Switch id="auto-close-issues" checked={value} onCheckedChange={onChange} disabled={isAutoClosePending || !canEditWorkspaceSettings} />
            )}
          />
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" aria-label="More information">
                  <QuestionIcon className="text-default-text h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContent className="max-w-[290px]">
                  <TooltipArrow className="fill-white drop-shadow-[0_1px_0_rgb(var(--grey-3))]" />
                  When off, submitting a Repair Record leaves linked issues open for manual closure.
                </TooltipContent>
              </TooltipPortal>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {canEditWorkspaceSettings && (
        <Button type="button" variant="success" className="w-fit" onClick={handleSave} disabled={isSaveDisabled} loading={isSavePending}>
          Save changes
        </Button>
      )}
    </form>
  );
}

function SettingsIssuesPage() {
  const { org } = useUserStore();

  const { data: issueTags } = useQuery(getIssueTagsQueryOptions({ orgId: org?.id }));

  const { data: issueCategories } = useQuery(getIssueCategoriesQueryOptions({ orgId: org?.id }));

  return (
    <div className="flex w-full max-w-[833px] flex-col gap-4 rounded-lg bg-white p-6 shadow-lg">
      <ScrollArea viewportClassName="pr-2">
        <div className="flex flex-col gap-4">
          <h1 className="text-default-text text-xl font-medium">Issues</h1>
          {(!issueCategories || !issueTags) && <IssueSettingsSkeleton />}
          {issueCategories && issueTags && <IssueSettingsForm defaultValues={{ categories: issueCategories, tags: issueTags }} />}
        </div>
      </ScrollArea>
    </div>
  );
}

export default SettingsIssuesPage;
